import { useState, useEffect } from 'react';
import ConfirmModal from '../../../../ConfirmModal.jsx';
import TaulukkoFooter from '../../TaulukkoFooter';
import TableLoadingOverlay from './TableLoadingOverlay';

// Pure view for the new v2-only "Rappausnäyte" testi type (no v1 equivalent).
// Structurally mirrors VetolujuusView.jsx (same eligible/rows/addRow/patchArvo
// props from useTestiTypeV2, same C/CH/inp style constants, same Rakenneosa+
// Tunnus columns, same "Ladataan…/Yritä uudelleen" loading-row pattern).
//
// All the min/max(/mediaani) fields here are DIRECT ENTRY — the person filling
// this in already has these summary numbers from their own lab report and
// just types them in, exactly like Vetolujuus's murtosyvyysFrom/murtosyvyysTo
// pair. There is no raw-reading list or computed aggregation anywhere in this
// block.

const C  = { border: '1px solid #adb5bd', padding: '4px 5px', fontSize: '0.82rem', verticalAlign: 'middle' };
// No whiteSpace:'nowrap' here (unlike other v2 tables' CH) — the "Verkon
// sijainti verkotuslaastin ulkopinnasta [mm]" header is ~40 characters, and
// nowrap forces that onto one unbreakable line that the browser will widen
// the whole table to fit even under table-layout:fixed, which is what was
// actually causing the horizontal scroll (not the input nesting depth).
const CH = { ...C, background: '#04aa00', color: '#fff', fontWeight: 700, textAlign: 'center' };
// minWidth: 0 throughout below is load-bearing, not decorative: flex items
// default to min-width:auto (their own content's natural size as a floor),
// and this cell nests flex containers 3 deep (layer list > one layer > its
// min/max row) — every other v2 table's cells nest at most 2 deep. Without
// overriding it at each level, that floor compounds up through the nesting
// and can render wider than the fixed-layout column actually allocated,
// which is what was forcing the whole table into horizontal scroll.
const inp = { border: '1px solid #ced4da', borderRadius: 3, padding: '3px 6px', fontSize: '0.9rem', width: '100%', minWidth: 0, background: '#fff', textAlign: 'center', boxSizing: 'border-box' };

const ROMAN = ['I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX', 'X'];
const romanFor = (n) => ROMAN[n - 1] ?? String(n);

// Same mobile card pattern as VetolujuusView.jsx/OhuthieView.jsx/
// KloridipitoisuusView.jsx — same rows-per-sample shape, one row becomes
// one card below 768px.
function useIsMobile(breakpoint = 768) {
  const [mobile, setMobile] = useState(() => window.innerWidth < breakpoint);
  useEffect(() => {
    const handler = () => setMobile(window.innerWidth < breakpoint);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, [breakpoint]);
  return mobile;
}

const cardStyle = { border: '1px solid #e2e4e7', borderRadius: 10, padding: '12px 14px', marginBottom: 12, background: '#fff' };
const cardFieldLabelStyle = { fontSize: '0.72rem', color: '#6c757d', flexShrink: 0, width: 150 };
const cardFieldRowStyle = (first) => ({ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '8px 0', borderTop: first ? 'none' : '1px solid #f1f3f5' });

function MinMax({ min, max, onMin, onMax }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 4, minWidth: 0 }}>
      <input style={{ ...inp, flex: '1 1 0', minWidth: 0 }} type="number" defaultValue={min} onBlur={e => onMin(e.target.value)} placeholder="min" />
      <span style={{ color: '#6c757d', flexShrink: 0 }}>–</span>
      <input style={{ ...inp, flex: '1 1 0', minWidth: 0 }} type="number" defaultValue={max} onBlur={e => onMax(e.target.value)} placeholder="max" />
    </div>
  );
}

export default function RappausnayteView({ eligible, rows, loading, addRow, patchArvo, caption, commitCaption, captionKohdeId, taulukkoNum = 1 }) {
  // Single shared modal for the whole table — reusable ConfirmModal.jsx
  // (used across the app for delete confirmations) rather than window.confirm.
  const [pendingRemove, setPendingRemove] = useState(null); // { t, layers, idx, label }

  const confirmRemoveLayer = () => {
    if (!pendingRemove) return;
    const { t, layers, idx } = pendingRemove;
    const next = layers.filter((_, i) => i !== idx);
    patchArvo(t, 'verkotuslaasti', next.length ? next : [{ kerros: 1, min: '', max: '', mediaani: '' }]);
    setPendingRemove(null);
  };

  const isMobile = useIsMobile();

  // Built once per sample, consumed by both the desktop <tr> and the mobile
  // card — kept inline (not hoisted out of the component) because the
  // layer add/patch/remove closures below need patchArvo/setPendingRemove
  // from this scope.
  const rowsJsx = eligible.map(s => {
    // Rows auto-create the moment a sample becomes eligible (see
    // useTestiTypeV2) — this only shows for the brief window before that
    // lands, or if creation failed and needs a retry.
    const t = (rows[s.id] || [])[0];
    const rakenneosaNimi = s.rakenneosa?.nimi ?? '—';
    if (!t) return isMobile ? (
      <div key={s.id} style={cardStyle}>
        <div style={{ fontWeight: 700, fontSize: '0.9rem', marginBottom: 6 }}>{rakenneosaNimi} <span style={{ color: '#198754' }}>· {s.nayte_tunnus}</span></div>
        <em style={{ color: '#adb5bd' }}>Ladataan… </em>
        <button type="button" onClick={() => addRow(s)} style={{ border: 'none', background: 'none', color: '#0d6efd', cursor: 'pointer', fontSize: '0.8rem' }}>Yritä uudelleen</button>
      </div>
    ) : (
      <tr key={s.id}>
        <td style={{ ...C, textAlign: 'center' }}>{rakenneosaNimi}</td>
        <td style={C}>{s.nayte_tunnus}</td>
        <td style={C} colSpan={4}>
          <em style={{ color: '#adb5bd' }}>Ladataan… </em>
          <button type="button" onClick={() => addRow(s)} style={{ border: 'none', background: 'none', color: '#0d6efd', cursor: 'pointer', fontSize: '0.8rem' }}>Yritä uudelleen</button>
        </td>
      </tr>
    );

    const a = t.arvo || {};
    const layers = a.verkotuslaasti?.length ? a.verkotuslaasti : [{ kerros: 1, min: '', max: '', mediaani: '' }];
    const showLayerLabel = layers.length > 1;

    const patchLayer = (idx, field, value) => {
      const next = layers.map((l, i) => i === idx ? { ...l, [field]: value } : l);
      patchArvo(t, 'verkotuslaasti', next);
    };
    const addLayer = () => {
      const next = [...layers, { kerros: layers.length + 1, min: '', max: '', mediaani: '' }];
      patchArvo(t, 'verkotuslaasti', next);
    };
    const removeLayer = (idx) => {
      setPendingRemove({ t, layers, idx, label: romanFor(layers[idx]?.kerros ?? idx + 1) });
    };

    const fields = [
      {
        label: 'Kokonaispaksuus min-max [mm]', node: (
          <MinMax min={a.kokonaispaksuusMin} max={a.kokonaispaksuusMax}
            onMin={v => patchArvo(t, 'kokonaispaksuusMin', v)} onMax={v => patchArvo(t, 'kokonaispaksuusMax', v)} />
        ),
      },
      {
        label: 'Pintalaasti min-max/mediaani [mm]', node: (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 3, minWidth: 0 }}>
            <MinMax min={a.pintalaastiMin} max={a.pintalaastiMax}
              onMin={v => patchArvo(t, 'pintalaastiMin', v)} onMax={v => patchArvo(t, 'pintalaastiMax', v)} />
            <input style={inp} type="number" defaultValue={a.pintalaastiMediaani} onBlur={e => patchArvo(t, 'pintalaastiMediaani', e.target.value)} placeholder="med." />
          </div>
        ),
      },
      {
        label: 'Verkotuslaasti min-max/mediaani [mm]', node: (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
            {layers.map((l, idx) => (
              <div key={idx} style={{ display: 'flex', flexDirection: 'column', gap: 3, minWidth: 0, ...(idx > 0 ? { borderTop: '1px dashed #dee2e6', paddingTop: 5 } : {}) }}>
                {showLayerLabel && (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 4 }}>
                    <span style={{ fontSize: '0.72rem', fontWeight: 700, color: '#6c757d' }}>{romanFor(l.kerros ?? idx + 1)} kerros</span>
                    <button type="button" onClick={() => removeLayer(idx)} title="Poista kerros"
                      style={{ border: 'none', background: 'none', color: '#adb5bd', cursor: 'pointer', fontSize: '0.85rem', lineHeight: 1, padding: 0 }}>×</button>
                  </div>
                )}
                <MinMax min={l.min} max={l.max} onMin={v => patchLayer(idx, 'min', v)} onMax={v => patchLayer(idx, 'max', v)} />
                <input style={inp} type="number" defaultValue={l.mediaani} onBlur={e => patchLayer(idx, 'mediaani', e.target.value)} placeholder="med." />
              </div>
            ))}
            <button type="button" onClick={addLayer} style={{ border: 'none', background: 'none', color: '#0d6efd', cursor: 'pointer', fontSize: '0.72rem', fontWeight: 600, alignSelf: 'flex-start' }}>+ Lisää kerros</button>
          </div>
        ),
      },
      { label: 'Verkon sijainti verkotuslaastin ulkopinnasta [mm]', node: <input style={inp} type="text" defaultValue={a.verkonSijainti} onBlur={e => patchArvo(t, 'verkonSijainti', e.target.value)} placeholder="esim. 1,2 ja 5,6" /> },
    ];

    return isMobile ? (
      <div key={t.id} style={cardStyle}>
        <div style={{ fontWeight: 700, fontSize: '0.9rem', marginBottom: 8 }}>{rakenneosaNimi} <span style={{ color: '#198754' }}>· {s.nayte_tunnus || '—'}</span></div>
        {fields.map((f, i) => (
          <div key={f.label} style={cardFieldRowStyle(i === 0)}>
            <div style={cardFieldLabelStyle}>{f.label}</div>
            <div style={{ flex: 1, minWidth: 0 }}>{f.node}</div>
          </div>
        ))}
      </div>
    ) : (
      <tr key={t.id}>
        <td style={{ ...C, textAlign: 'center', color: '#495057', overflow: 'hidden', textOverflow: 'ellipsis' }}>{s.rakenneosa?.nimi ?? <span style={{ color: '#adb5bd', fontStyle: 'italic' }}>—</span>}</td>
        <td style={{ ...C, textAlign: 'center', overflow: 'hidden', textOverflow: 'ellipsis' }}>{s.nayte_tunnus || <span style={{ color: '#adb5bd', fontStyle: 'italic' }}>—</span>}</td>
        {fields.map(f => <td key={f.label} style={{ ...C, minWidth: 0, overflow: 'hidden' }}>{f.node}</td>)}
      </tr>
    );
  });

  return (
    <div style={{ overflowX: 'auto', position: 'relative', minHeight: loading ? 80 : undefined }}>
      <TableLoadingOverlay show={loading} />
      {eligible.length === 0 ? (
        <div style={{ color: '#6c757d', fontSize: '0.82rem' }}>Ei näytteitä joilla on "Rappaus" valittuna testiksi (Poränäyte-lohko).</div>
      ) : isMobile ? (
        <div>{rowsJsx}</div>
      ) : (
        <table style={{ borderCollapse: 'collapse', width: '100%', tableLayout: 'fixed' }}>
          <colgroup>
            <col style={{ width: '11%' }} />
            <col style={{ width: '8%' }} />
            <col style={{ width: '17%' }} />
            <col style={{ width: '21%' }} />
            <col style={{ width: '22%' }} />
            <col style={{ width: '21%' }} />
          </colgroup>
          <thead>
            <tr>
              <th style={CH}>Rakenneosa</th>
              <th style={CH}>Tunnus</th>
              <th style={CH}>Kokonaispaksuus<br />min-max [mm]</th>
              <th style={CH}>Pintalaasti<br />min-max/mediaani [mm]</th>
              <th style={CH}>Verkotuslaasti<br />min-max/mediaani [mm]</th>
              <th style={CH}>Verkon sijainti<br />verkotuslaastin<br />ulkopinnasta [mm]</th>
            </tr>
          </thead>
          <tbody>{rowsJsx}</tbody>
        </table>
      )}
      {/* kohdeId keyed off captionKohdeId (the already-loaded testi_caption
          row), not tkId — see PeitepaksuusView for why. */}
      <TaulukkoFooter taulukkoNum={taulukkoNum} kohdeId={captionKohdeId ?? 'preview'} caption={caption} onChange={commitCaption} />
      <ConfirmModal
        show={!!pendingRemove}
        onClose={() => setPendingRemove(null)}
        onConfirm={confirmRemoveLayer}
        title="Poista kerros"
        message={pendingRemove ? `Haluatko varmasti poistaa ${pendingRemove.label} kerroksen?` : ''}
        confirmText="Poista"
        cancelText="Peru"
      />
    </div>
  );
}
